import { ref } from "vue";
import L from "leaflet";
import type { Map as LeafletMap } from "leaflet";
import { API_BASE } from "@/config";

export interface VesselInfo {
  shipVoy: string;    // vessel call sign e.g. "STQZ"
  shipYear: string;
  berth: string;      // "BERTH1" / "BERTH2" — matches yard_area area_id
  shipName: string | null;
  eta: string | null;
  ata: string | null;
  etd: string | null;
}

export interface VesselFilter {
  year?: string;    // ShipYear e.g. "2025"; omit = no year filter
  shipId?: string;  // vessel permanent ID; omit = all
  shipVoy?: string; // voyage call sign e.g. "STQZ"; omit = all
}

// SVG viewBox: 317 × 69 (ship faces East = 90° geographic)
const SHIP_ASPECT = 317 / 69;
const MAX_COLS = 3;

// ── Geometry helpers ───────────────────────────────────────────────────────────

function polygonCentroid(ring: number[][]): [number, number] {
  const pts =
    ring.length > 1 &&
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1]
      ? ring.slice(0, -1)
      : ring;
  const lat = pts.reduce((s, p) => s + (p[1] ?? 0), 0) / pts.length;
  const lng = pts.reduce((s, p) => s + (p[0] ?? 0), 0) / pts.length;
  return [lat, lng];
}

/** Geographic bearing (0-360°, clockwise from North) of the berth's long axis (P0→P1). */
function berthBearing(ring: number[][]): number {
  const p0 = ring[0],
    p1 = ring[1];
  if (!p0 || !p1) return 90;
  const midLat = ((p0[1] ?? 0) + (p1[1] ?? 0)) / 2;
  const dE = ((p1[0] ?? 0) - (p0[0] ?? 0)) * Math.cos((midLat * Math.PI) / 180);
  const dN = (p1[1] ?? 0) - (p0[1] ?? 0);
  return ((Math.atan2(dE, dN) * 180) / Math.PI + 360) % 360;
}

/** Max pairwise vertex distance in metres ≈ berth long-axis length. */
function berthLengthM(ring: number[][]): number {
  const pts = ring.slice(0, -1);
  let maxDist = 80;
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const p = pts[i],
        q = pts[j];
      if (!p || !q) continue;
      const midLat = ((p[1] ?? 0) + (q[1] ?? 0)) / 2;
      const dE =
        ((q[0] ?? 0) - (p[0] ?? 0)) *
        Math.cos((midLat * Math.PI) / 180) *
        111_000;
      const dN = ((q[1] ?? 0) - (p[1] ?? 0)) * 111_000;
      const d = Math.sqrt(dE * dE + dN * dN);
      if (d > maxDist) maxDist = d;
    }
  }
  return maxDist;
}

// ── SVGOverlay builder ─────────────────────────────────────────────────────────

/**
 * Create an L.SVGOverlay for one vessel slot at a berth.
 *
 * The overlay bounds are the minimal axis-aligned rectangle that contains
 * the rotated ship, so nothing is clipped.
 */
function makeVesselOverlay(
  centLat: number,
  centLng: number,
  slotLengthM: number,
  berthBearingDeg: number,
  _vessel: VesselInfo,
  onClick: () => void,
): L.SVGOverlay {
  const cosLat = Math.cos((centLat * Math.PI) / 180);
  const θRad = (berthBearingDeg * Math.PI) / 180;
  const cosT = Math.abs(Math.cos(θRad));
  const sinT = Math.abs(Math.sin(θRad));

  const gapRatio = 0.95;
  const shipW = slotLengthM * gapRatio;
  const shipH = slotLengthM / SHIP_ASPECT;

  // Axis-aligned bounding box that fully contains the rotated ship (in metres)
  const bbWm = shipW * cosT + shipH * sinT;
  const bbHm = shipW * sinT + shipH * cosT;

  const halfLng = bbWm / 2 / (111_000 * cosLat);
  const halfLat = bbHm / 2 / 111_000;
  const bounds = L.latLngBounds(
    [centLat - halfLat, centLng - halfLng],
    [centLat + halfLat, centLng + halfLng],
  );

  // SVG rotation: ship faces East (90°) → rotate by (berthBearingDeg - 90°) CW
  // SVG rotation: ship faces West (90°) → rotate by (berthBearingDeg + 90°) CCW
  const svgRot = berthBearingDeg + 90;
  const cx = bbWm / 2,
    cy = bbHm / 2;
  const imgX = cx - shipW / 2,
    imgY = cy - shipH / 2;

  const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svgEl.setAttribute("viewBox", `0 0 ${bbWm.toFixed(2)} ${bbHm.toFixed(2)}`);
  svgEl.setAttribute("preserveAspectRatio", "none");

  const img = document.createElementNS("http://www.w3.org/2000/svg", "image");
  img.setAttribute("href", "/icons/container_ship_icon.svg");
  img.setAttribute("x", imgX.toFixed(2));
  img.setAttribute("y", imgY.toFixed(2));
  img.setAttribute("width", shipW.toFixed(2));
  img.setAttribute("height", shipH.toFixed(2));
  img.setAttribute(
    "transform",
    `rotate(${svgRot.toFixed(1)}, ${cx.toFixed(2)}, ${cy.toFixed(2)})`,
  );
  img.setAttribute("preserveAspectRatio", "none");
  svgEl.appendChild(img);

  const ov = new L.SVGOverlay(svgEl, bounds, {
    interactive: true,
    opacity: 0.9,
    className: "dvp-vessel-overlay",
    zIndex: 300,
  } as L.ImageOverlayOptions);
  ov.on("click", (e) => {
    L.DomEvent.stopPropagation(e);
    onClick();
  });
  return ov;
}

/**
 * Compute centre lat/lng for each vessel slot, distributed along the berth's
 * actual geographic axis (not just the E-W longitude axis).
 *
 * Ship size is always 1/MAX_COLS of berth length so a single vessel is not
 * oversized. Ships are centred as a group along the long axis, and shifted
 * toward the seaward (P0→P3) edge of the berth polygon.
 */
function vesselSlotCentres(
  feature: GeoJSON.Feature,
  count: number,
): Array<{
  lat: number;
  lng: number;
  slotLengthM: number;
  berthBearingDeg: number;
}> {
  const ring = (feature.geometry as GeoJSON.MultiPolygon).coordinates[0]?.[0];
  if (!ring) return [];

  const [centLat, centLng] = polygonCentroid(ring);
  const bearingDeg = berthBearing(ring);
  const totalLen = berthLengthM(ring);
  const cols = Math.min(count, MAX_COLS);

  // Fixed ship size = 1/MAX_COLS of berth — keeps size consistent for 1-3 vessels
  const slotLen = totalLen / MAX_COLS;

  const cosLat = Math.cos((centLat * Math.PI) / 180);
  const bearingRad = (bearingDeg * Math.PI) / 180;

  // Unit vector along berth long axis (in degree space)
  const dLngPerM = Math.sin(bearingRad) / (111_000 * cosLat);
  const dLatPerM = Math.cos(bearingRad) / 111_000;

  // Centre the group of `cols` ships symmetrically in the berth
  const startOffset = -((cols - 1) * slotLen) / 2;

  // Seaward offset: shift ships to the outer (water-facing) edge of the berth.
  // P0→P3 (ring[0]→ring[3]) is the cross-berth direction toward the sea.
  let seawdLat = 0;
  let seawdLng = 0;
  const p0 = ring[0];
  const p3 = ring[3];
  if (p0 && p3) {
    const dLng_cross = (p3[0] ?? 0) - (p0[0] ?? 0);
    const dLat_cross = (p3[1] ?? 0) - (p0[1] ?? 0);
    const dE_cross = dLng_cross * cosLat * 111_000;
    const dN_cross = dLat_cross * 111_000;
    const widthM = Math.sqrt(dE_cross * dE_cross + dN_cross * dN_cross);
    if (widthM > 0) {
      const shipH = slotLen / SHIP_ASPECT;
      // Offset so ship outer edge aligns with seaward polygon edge
      const offsetM = Math.max(0, widthM / 2 - shipH / 2);
      seawdLat = (dLat_cross * offsetM) / widthM;
      seawdLng = (dLng_cross * offsetM) / widthM;
    }
  }

  return Array.from({ length: count }, (_, i) => {
    const col = i % cols;
    return {
      lat: centLat + (startOffset + col * slotLen) * dLatPerM + seawdLat,
      lng: centLng + (startOffset + col * slotLen) * dLngPerM + seawdLng,
      slotLengthM: slotLen,
      berthBearingDeg: bearingDeg,
    };
  });
}

// ── Composable ─────────────────────────────────────────────────────────────────

export function useVesselLayer(
  getMap: () => LeafletMap | null,
  onSelect?: (vessel: VesselInfo) => void,
) {
  const vessels = ref<VesselInfo[]>([]);
  let activeFilter: VesselFilter = {};

  const berthFeatures = new Map<string, GeoJSON.Feature>();
  const overlays: L.SVGOverlay[] = [];

  function setBerthFeatures(features: GeoJSON.Feature[]) {
    berthFeatures.clear();
    for (const f of features) {
      const areaId = f.properties?.area_id as string | undefined;
      if (f.properties?.area_type === "BERTH" && areaId) {
        berthFeatures.set(areaId, f);
      }
    }
  }

  function clearOverlays() {
    const map = getMap();
    for (const ov of overlays) map?.removeLayer(ov);
    overlays.length = 0;
  }

  // SVGOverlay inherits ImageOverlay._reset() which is called on viewreset/zoom
  // but NOT on leaflet-rotate's 'rotate' event — call it manually.
  function onMapRotate() {
    for (const ov of overlays)
      (ov as unknown as { _reset?(): void })._reset?.();
  }

  function buildOverlays() {
    clearOverlays();
    const map = getMap();
    if (!map) return;

    // Group vessels by berth
    const byBerth = new Map<string, VesselInfo[]>();
    for (const v of vessels.value) {
      const list = byBerth.get(v.berth) ?? [];
      list.push(v);
      byBerth.set(v.berth, list);
    }

    for (const [berthId, berthVessels] of byBerth) {
      const feature = berthFeatures.get(berthId);
      if (!feature) continue;

      const slots = vesselSlotCentres(feature, berthVessels.length);
      berthVessels.forEach((vessel, idx) => {
        const slot = slots[idx];
        if (!slot) return;
        const ov = makeVesselOverlay(
          slot.lat,
          slot.lng,
          slot.slotLengthM,
          slot.berthBearingDeg,
          vessel,
          () => onSelect?.(vessel),
        );
        ov.addTo(map);
        overlays.push(ov);
      });
    }
  }

  async function refresh() {
    try {
      const params = new URLSearchParams();
      if (activeFilter.year)    params.set("year",    activeFilter.year);
      if (activeFilter.shipId)  params.set("shipId",  activeFilter.shipId);
      if (activeFilter.shipVoy) params.set("shipVoy", activeFilter.shipVoy);
      const qs = params.size > 0 ? `?${params}` : "";
      const res = await fetch(`${API_BASE}/api/tos/vessels${qs}`);
      if (!res.ok) return;
      vessels.value = await res.json();
      buildOverlays();
    } catch {
      /* backend unavailable */
    }
  }

  function start(filter: VesselFilter = {}) {
    activeFilter = { ...filter };
    const map = getMap();
    map?.on("rotate", onMapRotate);
    refresh();
  }

  function stop() {
    const map = getMap();
    map?.off("rotate", onMapRotate);
    clearOverlays();
    vessels.value = [];
  }

  /** Update filter and re-fetch without restarting the layer. */
  function setFilter(filter: VesselFilter) {
    activeFilter = { ...filter };
    refresh();
  }

  return { setBerthFeatures, start, stop, refresh, setFilter };
}
